import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axiosInstance from '../apiServices/axios';
import {
    TENT_ADD_MEMBERS,
    TENT_ATTACHMENT,
    TENT_CONVERSATION,
    TENT_CONVERSATIONS,
    TENT_LEAVE,
    TENT_MARK_READ,
    TENT_MEMBERS,
    TENT_MESSAGES,
    TENT_REMOVE_MEMBER,
    TENT_SEND_EMAIL,
} from '../apiServices/api_routes';

export const GetTentMembers = createAsyncThunk('tent/GET_MEMBERS', async (_, { rejectWithValue }) => {
    try {
        const projectId = localStorage.getItem('project_id');
        const { data } = await axiosInstance.get(TENT_MEMBERS(projectId));
        return data;
    } catch (error) {
        return rejectWithValue(error?.response?.data?.detail || 'Failed to load The Tent directory');
    }
});

export const SendTentEmail = createAsyncThunk(
    'tent/SEND_EMAIL',
    async ({ toUserIds, ccUserIds, subject, messageHtml }, { rejectWithValue }) => {
        try {
            const projectId = localStorage.getItem('project_id');
            const { data } = await axiosInstance.post(TENT_SEND_EMAIL(projectId), {
                to_user_ids: toUserIds,
                cc_user_ids: ccUserIds || [],
                subject,
                message_html: messageHtml,
            });
            return data;
        } catch (error) {
            const detail = error?.response?.data?.detail;
            return rejectWithValue(typeof detail === 'string' ? detail : 'Email could not be sent. Please try again.');
        }
    },
);

export const GetTentConversations = createAsyncThunk('tent/GET_CONVERSATIONS', async (_, { rejectWithValue }) => {
    try {
        const projectId = localStorage.getItem('project_id');
        const { data } = await axiosInstance.get(TENT_CONVERSATIONS(projectId));
        return data;
    } catch (error) {
        return rejectWithValue(error?.response?.data?.detail || 'Failed to load conversations');
    }
});

export const OpenDirectChat = createAsyncThunk('tent/OPEN_DIRECT', async (otherUserId, { rejectWithValue }) => {
    try {
        const projectId = localStorage.getItem('project_id');
        const { data } = await axiosInstance.post(TENT_CONVERSATIONS(projectId), {
            conversation_type: 'direct',
            other_user_id: otherUserId,
        });
        return data;
    } catch (error) {
        return rejectWithValue(error?.response?.data?.detail || 'Could not open chat');
    }
});

export const CreateGroupChat = createAsyncThunk(
    'tent/CREATE_GROUP',
    async ({ name, memberIds }, { rejectWithValue }) => {
        try {
            const projectId = localStorage.getItem('project_id');
            const { data } = await axiosInstance.post(TENT_CONVERSATIONS(projectId), {
                conversation_type: 'group',
                name,
                member_ids: memberIds || [],
            });
            return data;
        } catch (error) {
            return rejectWithValue(error?.response?.data?.detail || error?.response?.data?.name || 'Could not create group');
        }
    },
);

export const GetTentMessages = createAsyncThunk(
    'tent/GET_MESSAGES',
    async ({ conversationId, before, after }, { rejectWithValue }) => {
        try {
            const projectId = localStorage.getItem('project_id');
            const params = {};
            if (before) params.before = before;
            if (after) params.after = after;
            const { data } = await axiosInstance.get(TENT_MESSAGES(projectId, conversationId), { params });
            // #region agent log
            fetch('http://127.0.0.1:7798/ingest/539ad776-defd-4048-9a51-bd9937645f01', {
                method: 'POST',
                keepalive: true,
                headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': '9d55d5' },
                body: JSON.stringify({
                    sessionId: '9d55d5',
                    runId: 'pre-fix',
                    hypothesisId: 'H3',
                    location: 'tentSlice.js:GetTentMessages',
                    message: 'messages response',
                    data: {
                        isArray: Array.isArray(data?.messages),
                        count: Array.isArray(data?.messages) ? data.messages.length : null,
                        keys: data && typeof data === 'object' ? Object.keys(data).slice(0, 8) : [],
                        before: Boolean(before),
                        after: Boolean(after),
                    },
                    timestamp: Date.now(),
                }),
            }).catch(() => {});
            // #endregion
            return { conversationId, before, after, ...data };
        } catch (error) {
            return rejectWithValue(error?.response?.data?.detail || 'Failed to load messages');
        }
    },
);

export const SendTentMessage = createAsyncThunk(
    'tent/SEND_MESSAGE',
    async ({ conversationId, body, clientMessageId, attachmentIds, mentionUserIds }, { rejectWithValue }) => {
        try {
            const projectId = localStorage.getItem('project_id');
            const { data } = await axiosInstance.post(TENT_MESSAGES(projectId, conversationId), {
                body: body || '',
                client_message_id: clientMessageId || '',
                attachment_ids: attachmentIds || [],
                mention_user_ids: mentionUserIds || [],
            });
            return { conversationId, message: data };
        } catch (error) {
            return rejectWithValue(error?.response?.data?.detail || 'Message could not be sent');
        }
    },
);

export const UploadTentAttachment = createAsyncThunk(
    'tent/UPLOAD_ATTACHMENT',
    async ({ conversationId, file }, { rejectWithValue }) => {
        try {
            const projectId = localStorage.getItem('project_id');
            const form = new FormData();
            form.append('file', file);
            const { data } = await axiosInstance.post(TENT_ATTACHMENT(projectId, conversationId), form, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return data;
        } catch (error) {
            const detail = error?.response?.data?.file || error?.response?.data?.detail;
            return rejectWithValue(typeof detail === 'string' ? detail : 'Upload failed');
        }
    },
);

export const MarkTentRead = createAsyncThunk(
    'tent/MARK_READ',
    async ({ conversationId, messageId }, { rejectWithValue }) => {
        try {
            const projectId = localStorage.getItem('project_id');
            const { data } = await axiosInstance.post(TENT_MARK_READ(projectId, conversationId), {
                message_id: messageId,
            });
            return data;
        } catch (error) {
            return rejectWithValue(error?.response?.data?.detail || 'Could not mark read');
        }
    },
);

export const AddTentMembers = createAsyncThunk(
    'tent/ADD_MEMBERS',
    async ({ conversationId, memberIds }, { rejectWithValue }) => {
        try {
            const projectId = localStorage.getItem('project_id');
            const { data } = await axiosInstance.post(TENT_ADD_MEMBERS(projectId, conversationId), {
                member_ids: memberIds,
            });
            return data;
        } catch (error) {
            return rejectWithValue(error?.response?.data?.detail || 'Could not add members');
        }
    },
);

export const RemoveTentMember = createAsyncThunk(
    'tent/REMOVE_MEMBER',
    async ({ conversationId, userId }, { rejectWithValue }) => {
        try {
            const projectId = localStorage.getItem('project_id');
            const { data } = await axiosInstance.delete(TENT_REMOVE_MEMBER(projectId, conversationId, userId));
            return data;
        } catch (error) {
            return rejectWithValue(error?.response?.data?.detail || 'Could not remove member');
        }
    },
);

export const LeaveTentConversation = createAsyncThunk(
    'tent/LEAVE',
    async (conversationId, { rejectWithValue }) => {
        try {
            const projectId = localStorage.getItem('project_id');
            const { data } = await axiosInstance.post(TENT_LEAVE(projectId, conversationId));
            return { conversationId, ...data };
        } catch (error) {
            return rejectWithValue(error?.response?.data?.detail || 'Could not leave conversation');
        }
    },
);

export const RenameTentGroup = createAsyncThunk(
    'tent/RENAME',
    async ({ conversationId, name }, { rejectWithValue }) => {
        try {
            const projectId = localStorage.getItem('project_id');
            const { data } = await axiosInstance.patch(TENT_CONVERSATION(projectId, conversationId), { name });
            return data;
        } catch (error) {
            return rejectWithValue(error?.response?.data?.detail || 'Could not rename group');
        }
    },
);

const mergeById = (existing, incoming, { prepend = false } = {}) => {
    const map = new Map();
    const order = [];
    const push = item => {
        if (!item?.id) return;
        if (!map.has(item.id)) order.push(item.id);
        map.set(item.id, item);
    };
    if (prepend) {
        (incoming || []).forEach(push);
        (existing || []).forEach(push);
    } else {
        (existing || []).forEach(push);
        (incoming || []).forEach(push);
    }
    return order.map(id => map.get(id));
};

const upsertConversation = (list, conversation) => {
    if (!conversation?.id) return list || [];
    const next = [...(list || [])];
    const index = next.findIndex(item => item.id === conversation.id);
    if (index >= 0) {
        next[index] = { ...next[index], ...conversation };
        const [item] = next.splice(index, 1);
        return [item, ...next];
    }
    return [conversation, ...next];
};

const initialState = {
    members: [],
    projectName: '',
    memberCount: 0,
    inboundEmail: '',
    loading: false,
    error: null,
    emailSending: false,
    view: 'people',
    conversations: [],
    unreadTotal: 0,
    conversationsLoading: false,
    selectedConversationId: null,
    messagesByConversation: {},
    messagesLoading: false,
    sendingMessage: false,
};

const tentSlice = createSlice({
    name: 'tent',
    initialState,
    reducers: {
        clearTentState: state => {
            Object.assign(state, initialState);
        },
        setTentView: (state, action) => {
            state.view = action.payload === 'chat' ? 'chat' : 'people';
        },
        selectConversation: (state, action) => {
            state.selectedConversationId = action.payload || null;
        },
        applyTentSocketEvent: (state, action) => {
            const { event, payload, fromSelf } = action.payload || {};
            if (!event || !payload) return;
            if (event === 'message.created') {
                const conversationId = payload.conversation_id;
                const existing = state.messagesByConversation[conversationId] || [];
                const alreadyHad = existing.some(msg => msg.id === payload.id);
                const withoutOptimistic = existing.filter(
                    msg => !(msg.optimistic && msg.client_message_id && msg.client_message_id === payload.client_message_id),
                );
                state.messagesByConversation[conversationId] = mergeById(withoutOptimistic, [payload]);
                const convo = state.conversations.find(item => item.id === conversationId);
                if (convo) {
                    convo.latest_message = payload;
                    // Own echoes / already-known messages must not bump unread.
                    if (
                        !fromSelf &&
                        !alreadyHad &&
                        state.selectedConversationId !== conversationId &&
                        payload.message_type !== 'system'
                    ) {
                        convo.unread_count = (convo.unread_count || 0) + 1;
                        state.unreadTotal += 1;
                    }
                    state.conversations = upsertConversation(state.conversations, convo);
                }
            }
            if (event === 'conversation.updated' || event === 'member.changed') {
                state.conversations = upsertConversation(state.conversations, payload);
            }
            if (event === 'read.updated') {
                const convo = state.conversations.find(item => item.id === payload.conversation_id);
                if (convo && payload.user_id) {
                    // only update unread for current viewer when payload matches their reads via MarkTentRead
                }
            }
        },
        addOptimisticMessage: (state, action) => {
            const { conversationId, message } = action.payload;
            const existing = state.messagesByConversation[conversationId] || [];
            state.messagesByConversation[conversationId] = [...existing, message];
        },
        failOptimisticMessage: (state, action) => {
            const { conversationId, clientMessageId } = action.payload;
            const existing = state.messagesByConversation[conversationId] || [];
            state.messagesByConversation[conversationId] = existing.map(msg =>
                msg.client_message_id === clientMessageId ? { ...msg, failed: true, optimistic: false } : msg,
            );
        },
    },
    extraReducers: builder => {
        builder
            .addCase(GetTentMembers.pending, state => {
                state.loading = true;
                state.error = null;
            })
            .addCase(GetTentMembers.fulfilled, (state, action) => {
                state.loading = false;
                state.members = action.payload?.members || [];
                state.projectName = action.payload?.project_name || '';
                state.memberCount = action.payload?.member_count || 0;
                state.inboundEmail = action.payload?.inbound_email || '';
            })
            .addCase(GetTentMembers.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to load members';
            })
            .addCase(SendTentEmail.pending, state => {
                state.emailSending = true;
            })
            .addCase(SendTentEmail.fulfilled, state => {
                state.emailSending = false;
            })
            .addCase(SendTentEmail.rejected, state => {
                state.emailSending = false;
            })
            .addCase(GetTentConversations.pending, state => {
                state.conversationsLoading = true;
            })
            .addCase(GetTentConversations.fulfilled, (state, action) => {
                state.conversationsLoading = false;
                state.conversations = action.payload?.conversations || [];
                state.unreadTotal = action.payload?.unread_total || 0;
            })
            .addCase(GetTentConversations.rejected, state => {
                state.conversationsLoading = false;
            })
            .addCase(OpenDirectChat.fulfilled, (state, action) => {
                state.conversations = upsertConversation(state.conversations, action.payload);
                state.selectedConversationId = action.payload.id;
                state.view = 'chat';
            })
            .addCase(CreateGroupChat.fulfilled, (state, action) => {
                state.conversations = upsertConversation(state.conversations, action.payload);
                state.selectedConversationId = action.payload.id;
                state.view = 'chat';
            })
            .addCase(GetTentMessages.pending, state => {
                state.messagesLoading = true;
            })
            .addCase(GetTentMessages.fulfilled, (state, action) => {
                state.messagesLoading = false;
                const { conversationId, messages, before, after } = action.payload;
                const existing = state.messagesByConversation[conversationId] || [];
                if (after) {
                    state.messagesByConversation[conversationId] = mergeById(existing, messages || []);
                } else if (before) {
                    state.messagesByConversation[conversationId] = mergeById(existing, messages || [], {
                        prepend: true,
                    });
                } else {
                    state.messagesByConversation[conversationId] = messages || [];
                }
            })
            .addCase(GetTentMessages.rejected, state => {
                state.messagesLoading = false;
            })
            .addCase(SendTentMessage.pending, state => {
                state.sendingMessage = true;
            })
            .addCase(SendTentMessage.fulfilled, (state, action) => {
                state.sendingMessage = false;
                const { conversationId, message } = action.payload;
                const existing = state.messagesByConversation[conversationId] || [];
                const withoutOptimistic = existing.filter(
                    msg =>
                        !(
                            msg.optimistic &&
                            msg.client_message_id &&
                            msg.client_message_id === message.client_message_id
                        ),
                );
                state.messagesByConversation[conversationId] = mergeById(withoutOptimistic, [message]);
                const convo = state.conversations.find(item => item.id === conversationId);
                if (convo) {
                    convo.latest_message = message;
                    state.conversations = upsertConversation(state.conversations, convo);
                }
            })
            .addCase(SendTentMessage.rejected, state => {
                state.sendingMessage = false;
            })
            .addCase(MarkTentRead.fulfilled, (state, action) => {
                const convo = state.conversations.find(item => item.id === action.payload.conversation_id);
                if (convo) {
                    const previous = convo.unread_count || 0;
                    convo.unread_count = action.payload.unread_count || 0;
                    state.unreadTotal = Math.max(0, state.unreadTotal - previous + convo.unread_count);
                }
            })
            .addCase(AddTentMembers.fulfilled, (state, action) => {
                state.conversations = upsertConversation(state.conversations, action.payload);
            })
            .addCase(RemoveTentMember.fulfilled, (state, action) => {
                state.conversations = upsertConversation(state.conversations, action.payload);
            })
            .addCase(RenameTentGroup.fulfilled, (state, action) => {
                state.conversations = upsertConversation(state.conversations, action.payload);
            })
            .addCase(LeaveTentConversation.fulfilled, (state, action) => {
                state.conversations = (state.conversations || []).filter(item => item.id !== action.payload.conversationId);
                if (state.selectedConversationId === action.payload.conversationId) {
                    state.selectedConversationId = state.conversations[0]?.id || null;
                }
            });
    },
});

export const {
    clearTentState,
    setTentView,
    selectConversation,
    applyTentSocketEvent,
    addOptimisticMessage,
    failOptimisticMessage,
} = tentSlice.actions;

const tentReducer = (state, action) => {
    // Older persisted Tent state has no chat maps. Reading them crashes the Chat screen.
    const normalized =
        state == null
            ? state
            : {
                  ...initialState,
                  ...state,
                  members: Array.isArray(state.members) ? state.members : [],
                  conversations: Array.isArray(state.conversations) ? state.conversations : [],
                  messagesByConversation:
                      state.messagesByConversation && typeof state.messagesByConversation === 'object'
                          ? state.messagesByConversation
                          : {},
              };
    try {
        return tentSlice.reducer(normalized, action);
    } catch (error) {
        // #region agent log
        fetch('http://127.0.0.1:7798/ingest/539ad776-defd-4048-9a51-bd9937645f01', {
            method: 'POST',
            keepalive: true,
            headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': '9d55d5' },
            body: JSON.stringify({
                sessionId: '9d55d5',
                runId: 'pre-fix',
                hypothesisId: 'H3',
                location: 'tentSlice.js:tentReducer',
                message: 'tent reducer threw',
                data: {
                    type: action?.type || '',
                    error: String(error?.message || 'reducer threw').slice(0, 300),
                    stack: String(error?.stack || '').slice(0, 500),
                },
                timestamp: Date.now(),
            }),
        }).catch(() => {});
        // #endregion
        throw error;
    }
};

export default tentReducer;
